if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: 'auto',
        loop: swiperOptions.loop !== false,
        resistanceRatio: 0.72,
        navigation: {
          nextEl: this.querySelector('.swiper-button--next') || this.parentElement?.querySelector('.swiper-button--next'),
          prevEl: this.querySelector('.swiper-button--prev') || this.parentElement?.querySelector('.swiper-button--prev')
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }
      this.initSlider();
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;
            this.parent.classList.remove('hidden');
            this.initSlider();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    initSlider(slider = this) {
      // Create unique IDs for this carousel instance
      const carouselId = `carousel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      this.setAttribute('data-carousel-id', carouselId);
      
      // Find navigation buttons - try multiple approaches
      let nextBtn = this.querySelector('.swiper-button--next');
      let prevBtn = this.querySelector('.swiper-button--prev');
      
      // If not found inside, try parent section
      if (!nextBtn || !prevBtn) {
        const section = this.closest('section') || this.closest('[data-section-id]');
        if (section) {
          nextBtn = nextBtn || section.querySelector('.swiper-button--next');
          prevBtn = prevBtn || section.querySelector('.swiper-button--prev');
        }
      }
      
      // Only assign to this carousel if buttons are not already assigned to another carousel
      if (nextBtn && !nextBtn.hasAttribute('data-carousel-id')) {
        nextBtn.setAttribute('data-carousel-id', carouselId);
        nextBtn.id = `next-${carouselId}`;
      }
      if (prevBtn && !prevBtn.hasAttribute('data-carousel-id')) {
        prevBtn.setAttribute('data-carousel-id', carouselId);
        prevBtn.id = `prev-${carouselId}`;
      }

      // Only loop when there are more slides than the widest view (4), otherwise Swiper loop misbehaves
      const slideCount = slider.querySelectorAll('.swiper-slide').length;
      this.sliderOptions.loop = this.sliderOptions.loop && slideCount > 4;

      // Update navigation options with unique IDs
      this.sliderOptions.navigation = {
        nextEl: (nextBtn && nextBtn.getAttribute('data-carousel-id') === carouselId) ? `#next-${carouselId}` : null,
        prevEl: (prevBtn && prevBtn.getAttribute('data-carousel-id') === carouselId) ? `#prev-${carouselId}` : null
      };

      this.slider = new Swiper(slider, this.sliderOptions);

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });
      const sliderButtons = [
        this.slider.navigation.prevEl,
        this.slider.navigation.nextEl
      ];

      sliderButtons.forEach((button, i) => {
        let type = 'Previous';

        if (i === 1) {
          type = 'Next';
        }

        button.setAttribute('aria-label', `${type} slide`);
        button.setAttribute(
          'aria-controls',
          this.slider.wrapperEl.id
        );
      });

      this.slider.wrapperEl.addEventListener('focusin', e => {
        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');
        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide =
          slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);

          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;
        // prettier-ignore
        const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);
        const isSlideVisible =
          slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) {
          return;
        }

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
